import Head from 'next/head';
import {ReactNode, createContext, useContext, useMemo} from 'react';
import {Country} from 'mkt-ssr/lib/globalization/types';
import {isServer} from 'mkt-ssr/lib/env';
import {Countries} from 'mkt-ssr/generated/globalization/countries';
import {isCountryCode} from 'mkt-ssr/lib/globalization/helpers';

const GeoIPCountryContext = createContext<Country | undefined>(undefined);

// eslint-disable-next-line @stripe-internal/stripe/no-mixed-react-exports
export function useGeoIPCountry() {
  // NB: This very intentionally does not use `useRequiredContext()`!
  // We may very well not have this value for a user, and as a result
  // this value is always going to be possible to be undefined. Furthermore
  // on the server this value will never be defined.
  return useContext(GeoIPCountryContext);
}

export function GeoIPCountryProvider({children}: {children: ReactNode}) {
  // NB: We have to attempt to read the
  // value from an existing DOM node because it
  // is injected by server middleware and this prevents
  // it from being overwritten/removed when the component
  // is remounted on the client.
  const geoIPCountry = useMemo(() => {
    // This value will never be present on the server
    // so don't even try.
    if (!isServer()) {
      const metaValue = document.querySelector<HTMLMetaElement>(
        'meta[name="request-country"]',
      )?.content;
      if (metaValue && metaValue.trim().length > 0) {
        if (isCountryCode(metaValue)) {
          return Countries[metaValue];
        }
      }
    }
  }, []);

  return (
    <>
      <Head>
        <meta name="request-country" content={geoIPCountry?.code || ''} />
      </Head>
      <GeoIPCountryContext.Provider value={geoIPCountry}>
        {children}
      </GeoIPCountryContext.Provider>
    </>
  );
}
